import React from "react";

import {
  BarChartOutlined,
  CallSplit,
  ColorLens,
  HomeOutlined,
  PaidOutlined,
  SettingsOutlined,
  Timeline,
} from "@mui/icons-material";
import DifferenceIcon from "@mui/icons-material/Difference";
import SpeedIcon from "@mui/icons-material/Speed";
import { SvgIcon } from "@mui/material";
import type { MainRoute } from "@src/types/navigation";

import { ConfigCompareDomain } from "../../domains/ConfigCompare";
import { SitewideDashboard } from "../../domains/Dashboard/Pages/AlwaysOnDashboard/SitewideDashboard";
import { DataReconciliationDashboard } from "../../domains/Dashboard/Pages/DataReconciliationDashboard/DataReconciliationDashboard";
import { DiscountsDashboard } from "../../domains/Dashboard/Pages/DiscountsDashboard/DiscountsDashboard";
import { OrdersDashboard } from "../../domains/Dashboard/Pages/OrdersDashboard/OrdersDashboard";
import { OverviewDashboard } from "../../domains/Dashboard/Pages/OverviewDashboard/OverviewDashboard";
import { ProductGroupsDashboard } from "../../domains/Dashboard/Pages/ProductGroupsDashboard/ProductGroupsDashboard";
import { ProfitDashboard } from "../../domains/Dashboard/Pages/ProfitDashboard/ProfitDashboard";
import { RevenueDashboard } from "../../domains/Dashboard/Pages/RevenueDashboard/RevenueDashboard";
import { ShippingDashboard } from "../../domains/Dashboard/Pages/ShippingDashboard/ShippingDashboard";
import { StatSigDashboard } from "../../domains/Dashboard/Pages/StatSigDashboard/StatSigDashboard";
import { SubscriptionsDashboard } from "../../domains/Dashboard/Pages/SubscriptionsDashboard/SubscriptionsDashboard";
import { TimeSeriesDashboard } from "../../domains/Dashboard/Pages/TimeSeriesDashboard/TimeSeriesDashboard";
import { VisitorsDashboard } from "../../domains/Dashboard/Pages/VisitorsDashboard/VisitorsDashboard";
import { HomeDashboard } from "../../domains/HomeDashboard";
import { PerformanceRoute } from "../../domains/Performance/PerformanceRoute";
import { PriceScraper } from "../../domains/PriceScraper";
import { Settings } from "../../domains/Settings";
import { IntegrationsRoute } from "../../domains/Settings/Integrations";
import { WidgetLibraryRoute } from "../../domains/WidgetLibrary/WidgetLibrary.route";
import { ExperiencesHomeRoute } from "../../features/experienceTests/pages/home/ExperiencesHome.route";
import { PersonalizationOverviewRoute } from "../../features/personalizations/pages/personalizationOverview/PersonalizationOverview.route";
import { WidgetLibraryIcon } from "../../images/Icons/WidgetLibraryIcon";
import {
  DASHBOARD_ROUTES,
  EXPERIMENT_ROUTES,
  GENERAL_ROUTES,
  PERSONALIZATION_ROUTES,
  SETTINGS_ROUTES,
  WIDGET_ROUTES,
} from "../../routes/routes";
import { IntegrationsIcon } from "../Navigation/NavIcons/IntegrationsIcon";

interface TopSideNavRoute {
  home: MainRoute;
  experiments: MainRoute;
  sitewide: MainRoute;
  analytics: MainRoute;
  widgetLibrary: MainRoute;
  priceScraper: MainRoute;
  personalizations: MainRoute;
}

interface BottomSideNavRoute {
  configCompare: MainRoute;
  integrations: MainRoute;
  settings: MainRoute;
  performance: MainRoute;
}

const TopRoutes = (): TopSideNavRoute => ({
  home: {
    path: GENERAL_ROUTES.Home,
    sidebarName: "Home",
    component: <HomeDashboard />,
    icon: <HomeOutlined />,
    routes: [],
  },
  experiments: {
    path: EXPERIMENT_ROUTES.Experiences,
    sidebarName: "A/B Tests",
    component: <ExperiencesHomeRoute />,
    icon: <CallSplit />,
    routes: [],
  },
  personalizations: {
    path: PERSONALIZATION_ROUTES.Personalizations,
    sidebarName: "Personalizations",
    component: <PersonalizationOverviewRoute />,
    icon: <ColorLens />,
    routes: [],
  },
  sitewide: {
    path: DASHBOARD_ROUTES.Dashboard_AlwaysOn,
    sidebarName: "Sitewide Analytics",
    component: <SitewideDashboard />,
    icon: <Timeline />,
    routes: [],
  },
  analytics: {
    path: DASHBOARD_ROUTES.Dashboard_Overview,
    sidebarName: "Test Analytics",
    component: <OverviewDashboard />,
    icon: <BarChartOutlined />,
    routes: [
      {
        path: DASHBOARD_ROUTES.Dashboard_Overview,
        sidebarName: "Overview",
        component: <OverviewDashboard />,
        icon: undefined,
      },
      {
        path: DASHBOARD_ROUTES.Dashboard_Revenue,
        sidebarName: "Revenue",
        component: <RevenueDashboard />,
        icon: undefined,
      },
      {
        path: DASHBOARD_ROUTES.Dashboard_Profit,
        sidebarName: "Profit",
        component: <ProfitDashboard />,
        icon: undefined,
      },
      {
        path: DASHBOARD_ROUTES.Dashboard_Shipping,
        sidebarName: "Shipping",
        component: <ShippingDashboard />,
        icon: undefined,
      },
      {
        path: DASHBOARD_ROUTES.Dashboard_Visitors,
        sidebarName: "Visitors",
        component: <VisitorsDashboard />,
        icon: undefined,
      },
      {
        path: DASHBOARD_ROUTES.Dashboard_Orders,
        sidebarName: "Orders Detail",
        component: <OrdersDashboard />,
        icon: undefined,
      },
      {
        path: DASHBOARD_ROUTES.Dashboard_Discounts,
        sidebarName: "Discounts",
        component: <DiscountsDashboard />,
        icon: undefined,
      },
      {
        path: DASHBOARD_ROUTES.Dashboard_ProductGroups,
        sidebarName: "Product Groups",
        component: <ProductGroupsDashboard />,
        icon: undefined,
      },
      {
        path: DASHBOARD_ROUTES.Dashboard_Trend,
        sidebarName: "Trend",
        component: <TimeSeriesDashboard />,
        icon: undefined,
      },
      {
        path: DASHBOARD_ROUTES.Dashboard_StatSig,
        sidebarName: "Stat Sig",
        component: <StatSigDashboard />,
        icon: undefined,
      },
      {
        path: DASHBOARD_ROUTES.Dashboard_Subscriptions,
        sidebarName: "Subscriptions",
        component: <SubscriptionsDashboard />,
        icon: undefined,
      },
      {
        path: DASHBOARD_ROUTES.Dashboard_Data_Reconciliation,
        sidebarName: "Data Reconciliation",
        component: <DataReconciliationDashboard />,
        icon: undefined,
      },
    ],
  },
  widgetLibrary: {
    path: WIDGET_ROUTES.WidgetLibrary_Experiences,
    sidebarName: "Widget Library",
    component: <WidgetLibraryRoute />,
    icon: <WidgetLibraryIcon />,
    routes: [
      {
        path: WIDGET_ROUTES.WidgetLibrary_Experiences,
        sidebarName: "Offer Widgets",
        component: <WidgetLibraryRoute />,
        icon: undefined,
      },
      {
        path: WIDGET_ROUTES.WidgetLibrary_Sitewide,
        sidebarName: "Sitewide Widgets",
        component: <WidgetLibraryRoute />,
        icon: undefined,
      },
    ],
  },
  priceScraper: {
    path: GENERAL_ROUTES.PriceScraper,
    sidebarName: "Price AI",
    component: <PriceScraper />,
    icon: <PaidOutlined />,
    routes: [],
  },
});

const BottomRoutes = (): BottomSideNavRoute => ({
  configCompare: {
    path: GENERAL_ROUTES.ConfigCompare,
    sidebarName: "Compare Configs",
    component: <ConfigCompareDomain />,
    icon: <DifferenceIcon />,
    routes: [],
  },
  settings: {
    path: SETTINGS_ROUTES.Settings,
    sidebarName: "Settings",
    component: <Settings />,
    icon: <SettingsOutlined />,
    routes: [],
  },
  performance: {
    path: GENERAL_ROUTES.Performance,
    sidebarName: "Performance",
    // @ts-ignore
    component: PerformanceRoute,
    icon: <SpeedIcon />,
    routes: [],
  },
  integrations: {
    path: GENERAL_ROUTES.Integrations,
    sidebarName: "Integrations",
    // @ts-ignore
    component: IntegrationsRoute,
    icon: <SvgIcon viewBox="0 0 19 12" component={IntegrationsIcon} />,
    routes: [],
  },
});

export { BottomRoutes, TopRoutes };
